import { DateFields } from '../entities/date-range.entity';
import { DatePart } from './date-range-validation.helper';

/**
 * A year/month-only range bound is stored as a full timestamp on the year/month edge so the backend
 * range stays inclusive: a start bound expands to the first instant (`YYYY-01-01T00:00:00.000` /
 * month's first day), an end bound to the last instant (`YYYY-12-31T23:59:59.999` / month's last day).
 * These predicates recognise those exact edges so the granularity the user entered can be recovered —
 * both for the humanised label and for re-seeding the date inputs when the filter is reopened.
 *
 * The one ambiguity — a literal first-of-month / last-of-month full date collapses to the coarser unit
 * — is accepted: for a date range the coarser value reads correctly there too.
 */
const isDayStart = (date: Date): boolean =>
    date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;

const isDayEnd = (date: Date): boolean =>
    date.getHours() === 23 && date.getMinutes() === 59 && date.getSeconds() === 59 && date.getMilliseconds() === 999;

const getLastDayOfMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

/** True when this fully-expanded bound sits on the whole-year edge for its side (start vs. end). */
export const isYearBound = (fields: DateFields, isStart: boolean): boolean => {
    if (fields.Date === null || fields.Year === null) {
        return false;
    }

    return isStart
        ? fields.Month === 1 && fields.Day === 1 && isDayStart(fields.Date)
        : fields.Month === 12 && fields.Day === 31 && isDayEnd(fields.Date);
};

/** True when this fully-expanded bound sits on the whole-month edge for its side (start vs. end). */
export const isMonthBound = (fields: DateFields, isStart: boolean): boolean => {
    if (fields.Date === null || fields.Year === null || fields.Month === null) {
        return false;
    }

    return isStart
        ? fields.Day === 1 && isDayStart(fields.Date)
        : fields.Day === getLastDayOfMonth(fields.Year, fields.Month) && isDayEnd(fields.Date);
};

/**
 * Collapse a fully-expanded range bound back to the granularity the user entered, nulling the finer
 * fields the inclusive-range expansion added (month + day for a year bound, day for a month bound).
 */
export const ReduceToEnteredDateFields = (fields: DateFields, isStart: boolean): DatePart => {
    if (isYearBound(fields, isStart)) {
        return { Year: fields.Year, Month: null, Day: null };
    }

    if (isMonthBound(fields, isStart)) {
        return { Year: fields.Year, Month: fields.Month, Day: null };
    }

    return { Year: fields.Year, Month: fields.Month, Day: fields.Day };
};
